import React from 'react';
import { useNavigate } from 'react-router-dom';

import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const ProjectCard = ({ project }) => {
  const navigate = useNavigate();

  const getStatusColor = (status) => {
    switch (status) {
      case 'approved':
        return 'text-success bg-success/10';
      case 'pending':
        return 'text-warning bg-warning/10';
      case 'rejected':
        return 'text-error bg-error/10';
      case 'in-review':
        return 'text-accent bg-accent/10';
      default:
        return 'text-muted-foreground bg-muted';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return 'CheckCircle';
      case 'pending':
        return 'Clock';
      case 'rejected':
        return 'XCircle';
      case 'in-review':
        return 'Eye';
      default:
        return 'Circle';
    }
  };

  const formatTimeAgo = (timestamp) => {
    const now = new Date();
    const diff = now - new Date(timestamp);
    const hours = Math.floor(diff / (1000 * 60 * 60));
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `${days}d ago`;
    if (hours > 0) return `${hours}h ago`;
    return 'Just now';
  };

  const handleViewProject = () => {
    // Navigate to client preview with project ID as URL parameter
    navigate(`/client-preview/proj_${project?.id || '001'}?client=${encodeURIComponent(project?.clientName || 'Unknown Client')}&type=${encodeURIComponent(project?.projectType || 'Custom Project')}`);
  };

  const handleShareProject = (event) => {
    // Create a shareable link for the project
    const shareUrl = `${window.location?.origin}/client-preview/proj_${project?.id || '001'}?client=${encodeURIComponent(project?.clientName || 'Unknown Client')}&type=${encodeURIComponent(project?.projectType || 'Custom Project')}`;
    const shareData = {
      title: `${project?.projectType} - ${project?.clientName}`,
      text: `Check out this jewelry design project for ${project?.clientName}`,
      url: shareUrl,
    };
    
    // Check if Web Share API is supported and available
    if (navigator?.share && navigator?.canShare && navigator?.canShare(shareData)) {
      // Use Web Share API with proper error handling
      navigator.share(shareData)?.then(() => {
          console.log('Successfully shared project');
        })?.catch((error) => {
          console.log('Error sharing:', error);
          // If Web Share API fails, fallback to clipboard
          fallbackToClipboard(shareUrl, event);
        });
    } else if (navigator?.share && !navigator?.canShare) {
      // Web Share API exists but can't share this data, try anyway
      navigator.share(shareData)?.then(() => {
          console.log('Successfully shared project');
        })?.catch((error) => {
          console.log('Web Share API failed:', error);
          // Fallback to clipboard
          fallbackToClipboard(shareUrl, event);
        });
    } else {
      // Web Share API not supported, use clipboard fallback
      fallbackToClipboard(shareUrl, event);
    }
  };

  const fallbackToClipboard = (shareUrl, event) => {
    // Try clipboard API first
    if (navigator?.clipboard && window?.isSecureContext) {
      navigator.clipboard?.writeText(shareUrl)?.then(() => {
          showCopySuccess(event?.target);
        })?.catch((clipboardError) => {
          console.log('Clipboard API failed:', clipboardError);
          // Final fallback to prompt
          promptFallback(shareUrl);
        });
    } else {
      // Use execCommand fallback for older browsers
      try {
        const textArea = document.createElement('textarea');
        textArea.value = shareUrl;
        textArea.style.position = 'fixed';
        textArea.style.opacity = '0';
        document.body?.appendChild(textArea);
        textArea?.select();
        textArea?.setSelectionRange(0, 99999); // For mobile devices
        
        const successful = document.execCommand('copy');
        document.body?.removeChild(textArea);
        
        if (successful) {
          showCopySuccess(event?.target);
        } else {
          promptFallback(shareUrl);
        }
      } catch (execCommandError) {
        console.log('execCommand failed:', execCommandError);
        promptFallback(shareUrl);
      }
    }
  };

  const showCopySuccess = (buttonElement) => {
    const button = buttonElement?.closest('button');
    if (button) {
      const originalText = button?.textContent;
      const originalIcon = button?.querySelector('svg');
      
      button.textContent = 'Copied!';
      button.style.color = '#10b981'; // Success green color
      
      setTimeout(() => {
        button.textContent = '';
        if (originalIcon) {
          button?.appendChild(originalIcon);
        }
        button?.insertAdjacentText('afterbegin', 'Share');
        button.style.color = ''; // Reset color
      }, 2000);
    }
  };

  const promptFallback = (shareUrl) => {
    // Final fallback: show prompt with the URL
    try {
      window.prompt('Copy this link to share the project:', shareUrl);
    } catch (promptError) {
      console.log('All share methods failed:', promptError);
      // Could show a modal or notification here as final fallback
      alert('Unable to share automatically. Please copy this URL manually: ' + shareUrl);
    }
  };

  const handleClientNameClick = () => {
    // Navigate to project details or client profile
    navigate(`/client-preview/proj_${project?.id || '001'}?client=${encodeURIComponent(project?.clientName || 'Unknown Client')}&type=${encodeURIComponent(project?.projectType || 'Custom Project')}`);
  };

  return (
    <div className="bg-card border border-border rounded-lg p-6 elevation-1 hover:elevation-2 transition-luxury">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative w-12 h-12 bg-muted rounded-full overflow-hidden cursor-pointer" onClick={handleClientNameClick}>
            <Image 
              src={project?.clientAvatar} 
              alt={project?.clientName}
              className="w-full h-full object-cover"
            />
            {project?.hasNewFeedback && project?.newFeedbackCount > 0 && (
              <div className="absolute -top-1 -right-1 min-w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-lg border-2 border-white">
                {project?.newFeedbackCount > 9 ? '9+' : project?.newFeedbackCount}
              </div>
            )}
          </div>
          <div>
            <h3 
              className="font-heading text-lg text-foreground cursor-pointer hover:text-accent transition-colors" 
              onClick={handleClientNameClick}
            >
              {project?.clientName}
            </h3>
            <p className="text-sm text-muted-foreground">{project?.projectType}</p>
          </div>
        </div>
        <div className="relative">
          <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(project?.status)}`}>
            <Icon name={getStatusIcon(project?.status)} size={12} />
            <span className="capitalize">{project?.status?.replace('-', ' ')}</span>
          </div>
          {project?.hasNewFeedback && project?.newFeedbackCount > 0 && (
            <div className="absolute -top-2 -right-2 min-w-5 h-5 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center px-1 shadow-lg animate-pulse">
              {project?.newFeedbackCount > 9 ? '9+' : project?.newFeedbackCount}
            </div>
          )}
        </div>
      </div>
      <div className="mb-4">
        <div className="flex items-center justify-between text-sm mb-2">
          <span className="text-muted-foreground">Progress</span>
          <span className="text-foreground font-medium">{project?.progress}%</span>
        </div>
        <div className="w-full bg-muted rounded-full h-2">
          <div 
            className="bg-accent h-2 rounded-full transition-luxury" 
            style={{ width: `${project?.progress}%` }}
          />
        </div>
      </div>
      <div className="flex items-center justify-between text-sm text-muted-foreground mb-4">
        <div className="flex items-center space-x-2">
          <Icon name="Calendar" size={14} />
          <span>Created {formatTimeAgo(project?.createdAt)}</span>
        </div>
        <div className="flex items-center space-x-2 relative">
          <Icon name="MessageSquare" size={14} />
          <span>{project?.feedbackCount} feedback</span>
          {project?.hasNewFeedback && project?.newFeedbackCount > 0 && (
            <div className="absolute -top-2 -right-2 min-w-4 h-4 bg-accent text-white text-xs font-bold rounded-full flex items-center justify-center px-1">
              {project?.newFeedbackCount > 9 ? '9+' : project?.newFeedbackCount}
            </div>
          )}
        </div>
      </div>
      {project?.lastActivity && (
        <div className="bg-muted/50 rounded-lg p-3 mb-4">
          <div className="flex items-center space-x-2 text-sm">
            <Icon name="Activity" size={14} className="text-accent" />
            <span className="text-foreground font-medium">Recent Activity</span>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{project?.lastActivity}</p>
          <p className="text-xs text-muted-foreground mt-1">{formatTimeAgo(project?.lastActivityTime)}</p>
        </div>
      )}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-2">
          {project?.hasNewFeedback && (
            <div className="flex items-center space-x-2">
              <div className="w-2 h-2 bg-accent rounded-full animate-pulse" />
              <div className="bg-accent/10 text-accent px-2 py-1 rounded-md text-xs font-medium border border-accent/20">
                {project?.newFeedbackCount > 0 ? `${project?.newFeedbackCount} new` : 'New feedback'}
              </div>
            </div>
          )}
          {!project?.hasNewFeedback && (
            <span className="text-sm text-muted-foreground">Up to date</span>
          )}
        </div>
        <div className="flex items-center space-x-2">
          <Button 
            variant="ghost" 
            size="sm" 
            iconName="Eye" 
            iconPosition="left"
            onClick={handleViewProject}
          >
            View
          </Button>
          <Button 
            variant="outline" 
            size="sm" 
            iconName="Share" 
            iconPosition="left"
            onClick={handleShareProject}
          >
            Share
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ProjectCard;